import { useNavigate, useLocation } from 'react-router-dom'
import { SwipeableDrawer, useMediaQuery } from '@mui/material'
import { ReactNode, useState, useContext, useEffect, useRef } from 'react'

import '../styles/dashboard.css'
import { useOnlineStatus } from '../hooks'
import { useTranslation } from 'react-i18next'
import { syncSessions } from '../lib/common.lib'
import Loader from '../components/common/loader'
import Dialog from '../components/common/dialog'
import AppBar from '../components/dashboard/app-bar'
import { AuthContext } from '../contexts/auth.context'
import { AppStateContext } from '../contexts/app-state.context'
import { DocumentsContext } from '../contexts/documents.context'
import DrawerContent from '../components/dashboard/drawer-content'
import { useSecureStorage } from '../contexts/secure-storage.context'
import { shouldUseSuiteLandingLogout, suiteSignOut } from '../sso/services/auth.service'
import { useSuiteAuth } from '../sso/contexts/suite-auth.context'
import { logInfo, logSessionSnapshot } from '../sso/utils/sso-logger'

const Dashboard = ({ children }: { children: ReactNode }) => {
  const drawerWidth = 240
  const navigate = useNavigate()
  const location = useLocation()
  const isOnline = useOnlineStatus()
  const { handleClearSecret } = useSecureStorage()
  const { isSync, removeAuth } = useContext(AuthContext)
  const { status: suiteAuthStatus } = useSuiteAuth()
  const sectionContainerRef = useRef<HTMLDivElement>(null)
  const { updateUploadingDocumentId } = useContext(DocumentsContext)
  const { isLoading, scrollToTop, setScrollToTop, setCurrentBanksData, updateLoadingStatus, updateBanksBalancesDataLoadedStatus } = useContext(AppStateContext)
  const { t } = useTranslation(['dashboard'])

  const [isClosing, setIsClosing] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [currentLocationPath, setCurrentLocationPath] = useState('')
  const [isLoadingData, setIsLoadingData] = useState<boolean>(() => Boolean(isLoading))
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const smDown = useMediaQuery((theme: any) => theme.breakpoints.down('sm'))

  useEffect(() => {
    setCurrentLocationPath(location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isSyncSession = syncSessions.find((syncSession) => {
      if (currentLocationPath !== '/' && currentLocationPath.indexOf(syncSession) !== -1) {
        return syncSession
      }

      return null
    })

    if (isSyncSession) {
      setIsLoadingData(() => isSync || false)
    }
    // eslint-disable-next-line no-sparse-arrays, react-hooks/exhaustive-deps
  }, [location, isSync, currentLocationPath])

  useEffect(() => {
    if (currentLocationPath !== location.pathname && updateLoadingStatus) {
      updateLoadingStatus(false)
      setCurrentLocationPath(location.pathname)
    }

    if (currentLocationPath !== '/' && updateUploadingDocumentId) {
      updateUploadingDocumentId('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, currentLocationPath])

  useEffect(() => {
    if (scrollToTop && sectionContainerRef && sectionContainerRef.current) {
      sectionContainerRef.current.scrollTop = 0
      setScrollToTop(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToTop])

  useEffect(() => {
    if (isLoading !== undefined) {
      setIsLoadingData(isLoading)
    }
  }, [isLoading])

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen)
    }
  }

  const handleConfirmCloseSession = () => {
    setOpenConfirmDialog(true)
  }

  const handleCloseSession = () => {
    logInfo('Dashboard.handleCloseSession — user confirmed logout')
    logSessionSnapshot('Dashboard.handleCloseSession')

    setIsLoadingData(true)
    removeAuth?.()
    handleClearSecret?.()
    setCurrentBanksData?.([])
    updateBanksBalancesDataLoadedStatus?.(false)

    if (shouldUseSuiteLandingLogout()) {
      logInfo('Dashboard.handleCloseSession — suite landing logout')
      void suiteSignOut()
      return
    }

    logInfo('Dashboard.handleCloseSession — legacy navigate to /')
    navigate('/', { replace: true })
  }

  return (
    <>
      {!isOnline && (
        <>
          <div className="waiting-for-connection-warning">{t('waitingForConnection')}</div>
          <div className="waiting-for-connection-overlay"></div>
        </>
      )}
      <main className="dashboard-container">
        <AppBar handleDrawerToggle={handleDrawerToggle} />
        <section className="content">
          <SwipeableDrawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={() => {
              setIsClosing(false)
            }}
            onClose={() => {
              setIsClosing(true)
              setMobileOpen(false)
            }}
            onOpen={() => {
              setIsClosing(false)
              setMobileOpen(true)
            }}
            ModalProps={{
              keepMounted: true
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: '#0e9384' }
            }}
          >
            <DrawerContent handleCloseSession={handleConfirmCloseSession} />
          </SwipeableDrawer>
          <SwipeableDrawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: '#0e9384' }
            }}
            open
            onClose={() => {
              setIsClosing(true)
              setMobileOpen(false)
            }}
            onOpen={() => {
              setIsClosing(false)
              setMobileOpen(true)
            }}
          >
            <DrawerContent handleCloseSession={handleConfirmCloseSession} />
          </SwipeableDrawer>
          <div
            ref={sectionContainerRef}
            className="section-content-container"
          >
            {isLoadingData && suiteAuthStatus !== 'hydrating' && (
              <Loader sx={smDown ? { bottom: 0, right: 0, top: 0, left: 0, position: 'fixed' } : { bottom: 0, right: 0, top: 0, width: `calc(100% - ${drawerWidth}px)`, position: 'fixed' }} />
            )}
            {children}
          </div>
        </section>
        {openConfirmDialog && (
          <Dialog
            dialogTitle={t('logout.title')}
            closeButtonText={t('logout.cancel')}
            actionButtonText={t('logout.confirm')}
            handleDialogAction={() => {
              handleCloseSession()
              setOpenConfirmDialog(false)
            }}
            handleCloseOptionAction={() => setOpenConfirmDialog(false)}
            handleDialogOpenState={(openState) => setOpenConfirmDialog(openState)}
          >
            <div className="dialog-content-text">{t('logout.message')}</div>
          </Dialog>
        )}
      </main>
    </>
  )
}

export default Dashboard
